import { useEffect, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Share2, Store } from "lucide-react";

import { Crumbs, DesktopShell } from "../../components/desktop/shell.tsx";
import { DesktopProductCard } from "../../components/desktop/product-card.tsx";
import { Empty, ErrorBlock, Spinner } from "../../components/ui.tsx";
import { SellerNote } from "../../components/product-grid.tsx";
import { cn } from "@/lib/utils";
import { api } from "../../api/client.ts";
import { useProducts } from "../../api/queries.ts";
import { shareOrigin } from "../../lib/site-config.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 店铺
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这是这个平台跟普通商城最不一样的一页。**
 *    普通商城里一件货全网一个价；这里同一件货在不同的店里价格不同 ——
 *    因为价格是「商品 × 看它的人」算出来的。
 *
 * 🔴 **归属说明放在店招里，跟店名同一屏。**
 *    它解释了"为什么这里的价是这个价"。手机版把它做成骑在店招上的一张卡；
 *    网页上店招本身就够宽，直接并排放在右边 —— 用户看店名的那一眼就看到了。
 *
 * ⚠️ 店主自己摆的货架（店铺分类）在网页上竖排在左侧，跟全站商品页同一个手法：
 *    横滑条是手机的形态，网页上一列扫得更快。
 */

interface ShopView {
  id: number;
  name: string;
  type: string;
  levelKey: string;
  displayName: string;
  inviteCode: string;
  orderCount: number;
  since: string;
  isMine: boolean;
  boundTo: { id: number; name: string } | null;
}

const TYPE_LABEL: Record<string, string> = { dealer: "经销商", distributor: "分销商" };

export function DesktopShop() {
  const { id } = useParams({ from: "/shop/$id" });
  const shopId = Number(id);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [catId, setCatId] = useState<number | null>(null);

  const {
    data: shop,
    isPending,
    error,
  } = useQuery({
    queryKey: ["shop", shopId],
    queryFn: () => api.get<ShopView>(`/shops/${shopId}`),
    retry: false,
  });

  /**
   * 这家店自己的分类条（「主推」「工装常用」）。
   * ⚠️ 跟顶部的全站品类是两回事 —— 这是**店主自己摆的货架**。
   * ⚠️ 只出有货的分类：建了还没放货的点进去是空的，不如不显示。
   */
  const shopCats = useQuery({
    queryKey: ["shop", shopId, "categories"],
    queryFn: () =>
      api.get<
        { id: number; name: string; productCount: number; platformCategoryId: number | null }[]
      >(`/shops/${shopId}/categories`),
    enabled: Boolean(shop),
    retry: false,
  });
  const activeCat = shopCats.data?.find((c) => c.id === catId) ?? null;

  /**
   * ⚠️ 带上 shopId —— 没归属的访问者要按这家店的链算价。
   *
   * 🔴 **两种货架用两种筛法**：
   *    - 绑了平台品类的（自动货架）→ 按 `categoryId` 筛，
   *      这样上游新上的货自己就进来了，不用店主再归一次类。
   *    - 自定义货架 → 按 `shopCategoryId` 走关联表。
   */
  const products = useProducts(
    {
      sort: "sales",
      limit: 30,
      shopId,
      ...(activeCat?.platformCategoryId
        ? { categoryId: activeCat.platformCategoryId }
        : { shopCategoryId: catId ?? undefined }),
    },
    Boolean(shop),
  );

  /**
   * 🔴 **进了别人的店要被送回自己的店，不是甩一个错就完。**
   *    一个用户只能看见自己的所属商家 —— 但他可能是点了别人转发的链接进来的，
   *    对他来说这是一次正常的点击，不该以一个红色报错收场。
   */
  const boundShopId = (error as { details?: { boundShopId?: number } } | null)?.details
    ?.boundShopId;
  useEffect(() => {
    if (boundShopId) {
      navigate({ to: "/shop/$id", params: { id: String(boundShopId) }, replace: true });
    }
  }, [boundShopId, navigate]);

  if (isPending || boundShopId)
    return (
      <DesktopShell>
        <Spinner compact />
      </DesktopShell>
    );
  if (error || !shop)
    return (
      <DesktopShell>
        <ErrorBlock error={error ?? new Error("店铺不存在")} />
      </DesktopShell>
    );

  const shareUrl = `${shareOrigin()}/shop/${shop.id}`;
  const cats = shopCats.data ?? [];

  return (
    <DesktopShell>
      <Crumbs items={[{ label: "首页", to: "/" }, { label: shop.displayName }]} />

      {/* ── 店招 ── */}
      <div className="mb-6 flex items-center gap-6 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-7 py-6">
        <span className="flex size-16 shrink-0 items-center justify-center rounded-2xl bg-white/20 backdrop-blur">
          <Store className="size-8 text-white" />
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-[24px] font-medium text-white">{shop.displayName}</h1>
          <div className="mt-2 flex items-center gap-2">
            <span className="rounded bg-white/20 px-2 py-0.5 text-[12px] text-white">
              {TYPE_LABEL[shop.type] ?? shop.type}
            </span>
            {shop.levelKey && (
              <span className="rounded bg-white/20 px-2 py-0.5 text-[12px] text-white">
                {shop.levelKey}
              </span>
            )}
            <span className="text-[13px] text-white/75 tabular-nums">
              成交 {shop.orderCount} 单
            </span>
          </div>
        </div>

        {/*
          🔴 归属说明 —— 这一页最要紧的信息，跟店名并排放，看店名的那一眼就看到。
        */}
        <div className="max-w-[340px] shrink-0 rounded-xl bg-white/15 px-4 py-3 backdrop-blur">
          <SellerNote
            shopName={shop.displayName}
            boundToName={shop.boundTo?.name}
            isMine={shop.isMine}
            className="!text-[13px] !text-white"
          />
          {!shop.boundTo && (
            <p className="mt-1.5 text-[12px] leading-relaxed text-white/75">
              专属服务商负责你的售后与咨询。一旦确定就不会被别人改，换人需要联系平台客服。
            </p>
          )}
        </div>
      </div>

      {/* 分享。⚠️ 这是分销商裂变的主要动作，要放在他自己能一眼看到的位置。 */}
      {shop.isMine && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-3.5">
          <Share2 className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-[14px] text-muted-foreground">
            {shareUrl}
          </span>
          <button
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-[13px] transition-colors hover:bg-secondary/70"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch {
                /** ⚠️ 非 https 下 clipboard 不可用，静默失败即可 —— 链接就在眼前，他能自己选。 */
              }
            }}
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            {copied ? "已复制" : "复制店铺链接"}
          </button>
        </div>
      )}

      <div
        className={cn(
          "grid items-start gap-8",
          cats.length > 1 ? "grid-cols-[200px_minmax(0,1fr)]" : "grid-cols-1",
        )}
      >
        {/*
          🔴 **店主自己摆的货架** —— 只有一个分类时不显示这一栏：
             一个"全部 + 主推"的切换毫无信息量，白占一列。
        */}
        {cats.length > 1 && (
          <aside className="sticky top-24 rounded-xl border border-border bg-card p-4">
            <p className="mb-2.5 text-[13px] font-medium">店内分类</p>
            <div className="space-y-0.5">
              {[{ id: 0, name: "全部", productCount: 0 }, ...cats].map((c) => {
                const active = (catId ?? 0) === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setCatId(c.id === 0 ? null : c.id)}
                    className={cn(
                      "flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[14px] transition-colors",
                      active
                        ? "bg-brand-50 font-medium text-brand-700"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                    )}
                  >
                    <span className="min-w-0 truncate">{c.name}</span>
                    {c.id !== 0 && (
                      <span className="shrink-0 text-[12px] opacity-60">{c.productCount}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>
        )}

        <div className="min-w-0">
          <div className="mb-4 flex items-baseline gap-3">
            <h2 className="text-[18px] font-medium">店内商品</h2>
            {products.data && (
              <span className="text-[14px] text-muted-foreground">
                共 {products.data.items.length} 件
              </span>
            )}
          </div>

          {products.isPending ? (
            <Spinner compact />
          ) : products.error ? (
            <ErrorBlock error={products.error} />
          ) : products.data.items.length === 0 ? (
            /* ⚠️ 筛出来是空的时候别说"还没上架" —— 那是两回事，用户会以为整家店是空的。 */
            catId ? (
              <Empty title="这个分类下还没有商品" hint="换一个分类看看" compact />
            ) : (
              <Empty title="这家店还没有上架商品" hint="平台正在补货，过两天再来看看" compact />
            )
          ) : (
            <div className={cn("grid gap-5", cats.length > 1 ? "grid-cols-4" : "grid-cols-5")}>
              {products.data.items.map((p) => (
                <DesktopProductCard
                  key={p.id}
                  p={p}
                  shop={{ id: shop.id, name: shop.displayName }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </DesktopShell>
  );
}
